import React, { useEffect, useState } from 'react';
import { addDoc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { memoryDoc, memoryTimelineColl } from '../db';
import { Screen, UserProfile } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

interface Memory {
  id: string;
  title: string;
  note: string;
  date: string;
  type: string;
  createdBy: string;
  createdByName: string;
}

const TYPES = ['İlkler', 'Buluşma', 'Kutlama', 'Sıradan ama güzel', 'Dönüm noktası'];

function formatDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function MemoryTimeline({ user, navigate }: Props) {
  const [items, setItems] = useState<Memory[]>([]);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [type, setType] = useState(TYPES[0]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(memoryTimelineColl(user.coupleId), orderBy('date', 'desc'));
    return onSnapshot(q, snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as Memory))));
  }, [user.coupleId]);

  const add = async () => {
    if (!title.trim() || !date) return;
    await addDoc(memoryTimelineColl(user.coupleId), {
      title: title.trim(),
      note: note.trim(),
      date,
      type,
      createdBy: user.uid,
      createdByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setTitle('');
    setNote('');
    setDate('');
    setType(TYPES[0]);
    setOpen(false);
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('memories')}>←</button>
        <h1 style={styles.title}>Anı Zaman Çizgisi</h1>
      </div>

      <div className="feature-page">
        <section className="feature-hero-card">
          <p className="eyebrow">Anı çizgisi</p>
          <h2>Birlikte biriken güzel anlar.</h2>
          <button className="feature-primary" onClick={() => setOpen(true)}>Anı ekle</button>
        </section>

        <section className="timeline-list">
          {items.map((item, index) => (
            <article key={item.id} className="timeline-item">
              <div className="timeline-dot">{index + 1}</div>
              <div className="timeline-card">
                <small>{formatDate(item.date)} · {item.type}</small>
                <h3>{item.title}</h3>
                {item.note && <p>{item.note}</p>}
                <div>
                  <span>{item.createdByName}</span>
                  {item.createdBy === user.uid && <button onClick={() => deleteDoc(memoryDoc(user.coupleId, item.id))}>Sil</button>}
                </div>
              </div>
            </article>
          ))}
          {items.length === 0 && <div className="empty-state">Henüz timeline anısı yok. İlk önemli günü ekleyelim.</div>}
        </section>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Anı ekle</h2>
            <p className="modal-sub">Bu ilişkinin haritasına küçük bir işaret bırak.</p>
            <input placeholder="Başlık" value={title} onChange={e => setTitle(e.target.value)} style={{ marginBottom: 10 }} />
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ marginBottom: 10 }} />
            <select value={type} onChange={e => setType(e.target.value)} style={styles.select}>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <textarea placeholder="Kısa not" value={note} onChange={e => setNote(e.target.value)} style={{ marginBottom: 12 }} />
            <button className="btn btn-primary" onClick={add}>Kaydet</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  select: {
    width: '100%',
    marginBottom: 10,
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.09)',
    background: 'rgba(255,255,255,0.045)',
    color: 'white',
    fontFamily: 'Inter, sans-serif',
  },
};
